import { useEffect, useState } from 'react';
import { Mail, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { TacticalMotif } from '@/components/tactical/PitchBackground';
import { toast } from 'sonner';
import { cmsApi, newsletterApi } from '@/lib/api';

const BultenPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await cmsApi.getNewsletter();
        setCms(res);
      } catch (error) {
        console.error('Failed to load newsletter CMS content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Lütfen email adresinizi girin.');
      return;
    }

    if (!email.includes('@')) {
      toast.error('Geçerli bir email adresi girin.');
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await newsletterApi.subscribe(email);
      setIsSubscribed(true);
      toast.success(res?.message || 'Bültene başarıyla abone oldunuz!');
    } catch (error) {
      console.error('Newsletter subscribe failed:', error);
      const msg = error.response?.data?.detail || 'Abonelik sırasında bir hata oluştu.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <section className="container-wide py-20 sm:py-28">
          <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </section>
      </Layout>
    );
  }

  if (isSubscribed) {
    return (
      <Layout>
        <section className="container-wide py-20 sm:py-28">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
            <h1 className="heading-section text-foreground mb-4">Hoş Geldiniz!</h1>
            <p className="text-muted-foreground mb-4">
              <strong className="text-foreground">{email}</strong> adresiyle bültene abone oldunuz.
            </p>
            <Button 
              onClick={() => {
                setIsSubscribed(false);
                setEmail('');
              }}
              variant="outline"
            >
              Başka Bir Email Ekle
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="container-wide py-16 sm:py-24">
        <div className="max-w-2xl mx-auto text-center">
          {/* Decorative */}
          <div className="flex justify-center mb-8 opacity-40">
            <TacticalMotif variant="arrows" className="h-8" />
          </div>

          <h1 className="heading-article text-foreground mb-4">
            {cms?.title || 'Taktik Bülten'}
          </h1>
          <p className="body-large text-muted-foreground mb-8">
            {cms?.subtitle || 'Haftalık yeni yazılar, taktik güncellemeler ve özel içerikler. Spam yok, sadece futbol.'}
          </p>

          {/* Subscription Form */}
          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Email adresiniz"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-background border-border h-12"
                />
              </div>
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6"
              >
                {isSubmitting ? (
                  'Kaydediliyor...'
                ) : (
                  <>
                    {cms?.cta_label || 'Abone Ol'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {cms?.helper_text || 'Dilediğiniz zaman abonelikten çıkabilirsiniz.'}
            </p>
          </form>
        </div>
      </section>

      {/* Horizontal Rule */}
      <div className="container-wide">
        <div className="h-px bg-border" />
      </div>

      {/* What to Expect */}
      <section className="container-wide py-16 sm:py-20">
        <div className="max-w-3xl mx-auto">
          <h2 className="heading-subsection text-foreground text-center mb-12">
            Bültende Neler Var?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-sm flex items-center justify-center mx-auto mb-4">
                <span className="font-serif text-xl font-bold text-accent">1</span>
              </div>
              <h3 className="font-serif font-bold text-foreground mb-2">Yeni Yazılar</h3>
              <p className="text-sm text-muted-foreground">
                Her yeni yazı yayınlandığında email ile bilgilendirilirsin.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-sm flex items-center justify-center mx-auto mb-4">
                <span className="font-serif text-xl font-bold text-accent">2</span>
              </div>
              <h3 className="font-serif font-bold text-foreground mb-2">Haftalık Özet</h3>
              <p className="text-sm text-muted-foreground">
                Haftanın en dikkat çekici taktik gelişmeleri ve analizleri.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-sm flex items-center justify-center mx-auto mb-4">
                <span className="font-serif text-xl font-bold text-accent">3</span>
              </div>
              <h3 className="font-serif font-bold text-foreground mb-2">Özel İçerikler</h3>
              <p className="text-sm text-muted-foreground">
                Sadece bülten abonelerine özel kısa analizler ve notlar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-muted/30">
        <div className="container-wide py-12">
          <div className="flex flex-wrap justify-center gap-12 text-center">
            <div>
              <p className="font-serif text-3xl font-bold text-foreground">10+</p>
              <p className="text-sm text-muted-foreground">Yazı</p>
            </div>
            <div>
              <p className="font-serif text-3xl font-bold text-foreground">12+</p>
              <p className="text-sm text-muted-foreground">Terim</p>
            </div>
            <div>
              <p className="font-serif text-3xl font-bold text-foreground">2</p>
              <p className="text-sm text-muted-foreground">Seri</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BultenPage;
